import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { db } from "../../../../../../firebase";
import { set, ref } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import {
  Autocomplete,
  Box,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Typography,
  useTheme,
  Stack,
  Checkbox,
  Radio,
  RadioGroup,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Formik } from "formik";
import * as yup from "yup";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import "../../style/booking.css";
import SnackBar from "../../../../../../components/snackbar/SnackBar";
import { tokens } from "../../../../../../theme";
import { getEmployeesFromEnterprise } from "../../../../../../api/users/employee/employeeApi";
import { getListEnterprises } from "../../../../../../api/enterprise/enterprise";
import {
  createAddress,
  getEnterpriseAddress,
} from "../../../../../../api/enterprise/address";
import { dateFormat, timeFormat } from "../../../../../../helpers/date/date";

import { sendICSEmail } from "../../../../../../api/resources/ics/ics";
import { getListTourModules } from "../../../tour/data/repository/tourRepo";
import { sendBookingEnterpriseNotificationUseCase } from "../../../../../../features/booking/enterpriseNotification/domain/usecases/sendBookingEnterpriseNotificationUseCase";
import { getRatesAndRoutesAddress } from "../../../../../../api/users/enterprise/enterpriseApi";
import { CreateBackupUseCase } from "../../../backup/usecases/CreateBackupOrder";
import { BookingModel } from "./model/BookingModel";
import { CoordinateModel } from "./model/CoordinateModel";
import { DateModel } from "./model/DateModel";
import { DriverBase } from "./model/DriverBase";
import { EmployeeModel } from "./model/EmployeeModel";
import { TourModel } from "./model/TourModel";
import { CreateDataSql } from "./dto/CreateDataSql";
import { CreateDataFirebase } from "./dto/CreateDataFirebase";
import {
  hasConsecutiveDuplicateRoutes,
  isSameRoute,
} from "./domain/routeValidation";
import {
  decodeAdminToken,
  getToken,
  getTokenRefresh,
} from "../../../../../../helpers/token/refreshToken";
import { getTarifaCityListUseCase } from "../../../../../../features/tarifas/global/domain/usecases/getTarifaCityListUseCase";
import { getTarifaEnterpriseCityByEnterpriseUseCase } from "../../../../../../features/tarifas/empresa/domain/usecases/getTarifaEnterpriseCityByEnterpriseUseCase";
import { getGlobalCityListUseCase } from "../../../../../../features/tarifas/enterprise_city/domain/usecases/getGlobalCityListUseCase";
import MapIcon from "@mui/icons-material/Map";
import { Lottie } from "lottie-react";

const ROUTES_LOTTIE_PATH = "/assets/routes.json";
const MAX_ROUTE_MODAL_ITEMS = 10;

function RoutesLottieIcon() {
  return (
    <Box>
      <Lottie
        src={ROUTES_LOTTIE_PATH}
        loop
        autoplay
        style={{ width: 30, height: 30 }}
      />
    </Box>
  );
}

function getRouteValues(routeModalList) {
  const routes = Array.isArray(routeModalList)
    ? routeModalList.filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  const destination = routes[routes.length - 1];
  const subRoutes = routes.slice(0, -1).map((route, index) => ({
    id: index + 1,
    name: route.name,
    lat: route.latitude,
    lng: route.longitude,
  }));

  return {
    destination,
    subRoutes,
  };
}

function RouteSortableItem({ route, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(route.routeId),
    index,
    group: "booking-business-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {route.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar ruta"
        onClick={() => onDelete(route.routeId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const BookingBusiness = ({ model }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const matchesSM = useMediaQuery(theme.breakpoints.down("sm"));
  const dataToken = getToken("adttkn");
  const { company_id } = decodeAdminToken(dataToken);

  const [employee, setEmployee] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [otherPersonName, setOtherPersonName] = useState("");
  const [otherPersonPhone, setOtherPersonPhone] = useState("");
  const [otherPersonNameInput, setOtherPersonNameInput] = useState("");
  const [otherPersonPhoneInput, setOtherPersonPhoneInput] = useState("");
  const [otherPersonModalOpen, setOtherPersonModalOpen] = useState(false);
  const [originMapModalOpen, setOriginMapModalOpen] = useState(false);
  const [originMapAddress, setOriginMapAddress] = useState("");
  const [originMapCoordinates, setOriginMapCoordinates] = useState("");
  const [originMapSaving, setOriginMapSaving] = useState(false);
  const [routesModalOpen, setRoutesModalOpen] = useState(false);
  const [routeModalList, setRouteModalList] = useState([]);
  const [routeModalDestinationToAdd, setRouteModalDestinationToAdd] =
    useState(null);
  const [routeModalDestinationInput, setRouteModalDestinationInput] =
    useState("");
  const [enterprise, setEnterprise] = useState(null);
  const [enterprises, setEnterprises] = useState([]);
  const [globalCityList, setGlobalCityList] = useState([]);
  const [ratesCity, setRatesCity] = useState([]);
  const [rateEnterpriseCity, setRateEnterpriseCity] = useState(null);
  const precioKmTemp = 1.8;

  const [addressOrigin, setAddressOrigin] = useState(null);
  const [addressDestination, setAddressDestination] = useState(null);
  const [addressApi, setAddressApi] = useState([]);
  const [routeRateMode, setRouteRateMode] = useState("direcciones");
  const [vehicleSelect, setVehicleSelect] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);

  const [dateEnd, setDateEnd] = useState(null);
  const [timeEnd, setTimeEnd] = useState(null);

  const [checked, setChecked] = useState(false);
  const handleChangeCheckBox = (event) => setChecked(event.target.checked);

  const [phoneActive, setPhoneActive] = useState(true);
  const changePhoneActive = (e) => setPhoneActive(e.target.checked);

  const [serviceList, setServiceList] = useState([]);
  const [route1, setRoute1] = useState("");
  const [indice, setIndice] = useState(-1);

  const handleOpenOtherPersonModal = () => {
    setOtherPersonNameInput(otherPersonName);
    setOtherPersonPhoneInput(otherPersonPhone);
    setOtherPersonModalOpen(true);
  };

  const handleCloseOtherPersonModal = () => {
    setOtherPersonModalOpen(false);
  };

  const handleAcceptOtherPerson = () => {
    setOtherPersonName(otherPersonNameInput.trim().toUpperCase());
    setOtherPersonPhone(otherPersonPhoneInput.trim());
    setOtherPersonModalOpen(false);
  };

  const handleClearOtherPerson = () => {
    setOtherPersonName("");
    setOtherPersonPhone("");
    setOtherPersonNameInput("");
    setOtherPersonPhoneInput("");
  };

  const handleOpenOriginMapModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOriginMapModalOpen(true);
  };

  const handleCloseOriginMapModal = () => {
    setOriginMapModalOpen(false);
  };

  const handleSaveOriginMapAddress = async () => {
    const addressName = originMapAddress.trim();
    const coordinates = originMapCoordinates.split(",");
    const latitude = Number(coordinates[0]);
    const longitude = Number(coordinates[1]);

    if (!addressName) {
      snackBarDialog("INGRESE DIRECCION", "error");
      return;
    }

    if (
      coordinates.length !== 2 ||
      Number.isNaN(latitude) ||
      Number.isNaN(longitude)
    ) {
      snackBarDialog("COORDENADAS INCORRECTAS", "error");
      return;
    }

    try {
      setOriginMapSaving(true);
      const token = getTokenRefresh();
      const address = {
        global: true,
        global_enterprise: false,
        url: "",
        name: addressName.toUpperCase(),
        latitude,
        longitude,
        enterprises_id: null,
        employees_id: null,
      };
      const response = await createAddress(token, address);
      const addressOption = {
        ...address,
        id: response?.data?.insertId ?? response?.data?.id ?? Date.now(),
        url_address: "",
      };

      setAddressApi((current) => [...current, addressOption]);
      setAddressOrigin(addressOption);
      setAddressDestination(null);
      setOriginMapAddress("");
      setOriginMapCoordinates("");
      setOriginMapModalOpen(false);
      snackBarDialog("DIRECCION AGREGADA");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE AGREGO DIRECCION", "error");
    } finally {
      setOriginMapSaving(false);
    }
  };

  const handleOpenRoutesModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setRouteModalList((current) => {
      if (current.length > 0) {
        return current;
      }

      return addressDestination
        ? [createRouteListItem(addressDestination)]
        : [];
    });
    setRouteModalDestinationToAdd(null);
    setRouteModalDestinationInput("");
    setRoutesModalOpen(true);
  };

  const handleCloseRoutesModal = () => {
    setRoutesModalOpen(false);
  };

  useEffect(() => {
    if (routeModalList.length === 0) {
      return;
    }

    setAddressDestination(routeModalList[routeModalList.length - 1]);
  }, [routeModalList]);

  const createRouteListItem = (destination) => ({
    ...destination,
    routeId: uuidv4(),
  });

  const handleDestinationSelect = (newValue) => {
    setAddressDestination(newValue);
    setRouteModalList(newValue ? [createRouteListItem(newValue)] : []);
  };

  const handleAddRouteDestination = (newValue) => {
    if (!newValue) {
      setRouteModalDestinationToAdd(null);
      return;
    }

    const newRoute = createRouteListItem(newValue);
    setRouteModalList((current) => {
      const lastRoute = current[current.length - 1];
      if (lastRoute && isSameRoute(lastRoute, newRoute)) {
        snackBarDialog("NO SE PERMITEN RUTAS IGUALES CONSECUTIVAS", "error");
        return current;
      }

      if (current.length >= MAX_ROUTE_MODAL_ITEMS) {
        snackBarDialog("MAXIMO 10 RUTAS", "error");
        return current;
      }

      return [...current, newRoute];
    });
    setRouteModalDestinationToAdd(null);
    setRouteModalDestinationInput("");
  };

  const handleRouteDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled) {
      return;
    }

    setRouteModalList((current) => {
      let sourceIndex = -1;
      let targetIndex = -1;

      if (isSortableOperation(operation)) {
        sourceIndex = operation.source.initialIndex;
        targetIndex = operation.source.index;
      } else {
        const sourceId = String(operation.source?.id ?? "");
        const targetId = String(operation.target?.id ?? "");

        sourceIndex = current.findIndex((route) => route.routeId === sourceId);
        targetIndex = current.findIndex((route) => route.routeId === targetId);
      }

      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= current.length ||
        targetIndex >= current.length ||
        sourceIndex === targetIndex
      ) {
        return current;
      }

      const next = [...current];
      const [movedRoute] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, movedRoute);
      if (hasConsecutiveDuplicateRoutes(next)) {
        snackBarDialog("NO SE PERMITEN RUTAS IGUALES CONSECUTIVAS", "error");
        return current;
      }
      return next;
    });
  };

  const handleDeleteRouteDestination = (routeId) => {
    setRouteModalList((current) => {
      const next = current.filter((route) => route.routeId !== routeId);
      if (hasConsecutiveDuplicateRoutes(next)) {
        snackBarDialog("NO SE PERMITEN RUTAS IGUALES CONSECUTIVAS", "error");
        return current;
      }
      return next;
    });
  };

  const handleAcceptRoutes = () => {
    setRoutesModalOpen(false);
  };

  const globalCityOptions = useMemo(() => {
    const normalizeCoordinate = (value) => {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    };

    return globalCityList.map((city) => ({
      id: city.id,
      name: city.name || "",
      displayName: city.name || "",
      latitude: normalizeCoordinate(city.latitude),
      longitude: normalizeCoordinate(city.longitude),
      url: "",
      url_address: "",
    }));
  }, [globalCityList]);

  const findCityRateForSelectedRoute = useCallback(
    (origin, destination) => {
      if (!origin || !destination) {
        return null;
      }

      const isSameCityRateRoute = (rate) =>
        Number(rate?.origin_id ?? rate?.originId) === Number(origin.id) &&
        Number(rate?.destination_id ?? rate?.destinationId) ===
          Number(destination.id);

      const enterpriseRates = Array.isArray(rateEnterpriseCity)
        ? rateEnterpriseCity
        : rateEnterpriseCity
          ? [rateEnterpriseCity]
          : [];
      const enterpriseRate = enterpriseRates.find(isSameCityRateRoute);

      if (enterpriseRate) {
        return enterpriseRate;
      }

      const globalRates = Array.isArray(ratesCity) ? ratesCity : [];
      return globalRates.find(isSameCityRateRoute) || null;
    },
    [rateEnterpriseCity, ratesCity],
  );

  const checkoutSchema = yup.object().shape({
    persons: yup.number().required("required"),
    booking: yup.number().required("required"),
    vehicle: yup.number().required("required"),
    note: yup.string(),
  });
  //razon social business_name
  const initialValues = {
    name: "",
    ruc: "",
    persons: 1,
    vehicle: "",
    booking: 1,
    note: "",
    price: 0,
    tax: 0,
  };

  useEffect(() => {
    const getEnterprises = async () => {
      try {
        const token = "";
        const response = await getListEnterprises(token, company_id);
        setEnterprises(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    getEnterprises();
  }, []);

  useEffect(() => {
    const getGlobalCities = async () => {
      try {
        const cities = await getGlobalCityListUseCase();
        setGlobalCityList(cities);
      } catch (error) {
        console.log(error);
        setGlobalCityList([]);
      }
    };
    getGlobalCities();
  }, []);

  useEffect(() => {
    const getRatesCity = async () => {
      if (routeRateMode !== "ciudades") {
        setRatesCity([]);
        return;
      }

      try {
        const response = await getTarifaCityListUseCase();
        setRatesCity(response);
      } catch (error) {
        console.log(error);
        setRatesCity([]);
      }
    };
    getRatesCity();
  }, [routeRateMode]);

  useEffect(() => {
    const getAddressEnterprise = async () => {
      if (routeRateMode !== "direcciones" || !enterprise?.id) {
        if (routeRateMode !== "direcciones") {
          setAddressApi([]);
        }
        return;
      }

      try {
        const token = "";
        setAddressApi([]);
        setAddressOrigin(null);
        setAddressDestination(null);
        const response = await getEnterpriseAddress(token, enterprise.id);
        console.log(response);
        setAddressApi(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    getAddressEnterprise();
  }, [enterprise, routeRateMode]);

  useEffect(() => {
    const getRateEnterpriseCity = async () => {
      if (routeRateMode !== "ciudades" || !enterprise?.id) {
        setRateEnterpriseCity(null);
        return;
      }

      try {
        const response = await getTarifaEnterpriseCityByEnterpriseUseCase(
          enterprise.id,
        );
        console.log(response);
        setRateEnterpriseCity(response);
      } catch (error) {
        console.log(error);
        setRateEnterpriseCity(null);
      }
    };
    getRateEnterpriseCity();
  }, [enterprise, routeRateMode]);

  useEffect(() => {
    setAddressOrigin(null);
    setAddressDestination(null);
  }, [routeRateMode]);

  //address/enterprise/:enterprise_id
  const handleServiceAdd = () => {
    if (indice === -1) {
      setServiceList([...serviceList, { service: "" }]);
      setIndice(indice + 1);
    } else if (serviceList[indice].service !== "") {
      setServiceList([...serviceList, { service: "" }]);
      setIndice(indice + 1);
      setRoute1("");
    } else {
      window.alert("INGRESE UNA RUTA ANTES DE CREAR OTRO CAMPO");
    }
  };

  const handleServiceRemove = (index) => {
    const list = [...serviceList];
    list.splice(index, 1);
    setServiceList(list);
    setIndice(indice - 1);
    console.log(indice);
    console.log(serviceList);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const getEmployees = async (id) => {
    try {
      const token = "asdfasd";
      setEmployees(null);
      setEmployee(null);
      const response = await getEmployeesFromEnterprise(token, id);
      setEmployees(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  const [routesEnterprise, setRoutesEnterprise] = useState([]);
  const getRatesAndRoutes = async (id) => {
    try {
      const resp = await getRatesAndRoutesAddress(id);
      console.log("routesEnterprise", resp);
      setRoutesEnterprise(resp.data.routes);
    } catch (error) {
      console.log(error);
    }
  };

  const createBookingFirebase = async (data) => {
    const dbRef = ref(db, `BookingBusiness/${data.uuid}`);
    await set(dbRef, data);
    snackBarDialog("Pedido Creado. Enviando Email...");
    setServiceList([]);

    if (data.booking_type == 1) {
      return await sendBookingEnterpriseNotificationUseCase(data);
    }
    return null;
  };

  const sendEmailBooking = async (values, counter) => {
    try {
      const newBooking = {
        counterBooking: counter,
        clientName: `${employee.name} ${employee.lastname}`,
        clientEmail: employee.email,
        clientPhone: employee.phone,
        origin: { name: addressOrigin.name },
        destination: { name: addressDestination.name },
        date: dateFormat(date.$d),
        time: timeFormat(time.$d),
        note: values.note ? values.note : "",
        persons: values.persons,
        vehicle: values.vehicle,
        web: "Business",
      };
      await sendICSEmail("token", newBooking);
    } catch (error) {
      console.log(error);
    }
  };

  const userType = {
    ENTERPRISE: 0,
    CLIENT: 1,
  };
  const createdBy = {
    ADMIN: "ADMIN",
  };
  const currencyList = {
    PEN: 1,
    USD: 2,
  };
  const travelStatus = {
    PENDING: "PENDIENTE",
  };

  const handleFormSubmit = async (values) => {
    const routeValues = getRouteValues(routeModalList);

    if (!enterprise) return snackBarDialog("SELECCIONE LA EMPRESA", "error");
    if (!employee) return snackBarDialog("SELECCIONE AL TRABAJADOR", "error");
    if (!addressOrigin.latitude || !addressOrigin.longitude)
      return snackBarDialog("SELECCIONE EL ORIGEN DEL SERVICIO", "error");
    if (
      !routeValues.destination?.latitude ||
      !routeValues.destination?.longitude
    )
      return snackBarDialog("SELECCIONE EL DESTINO DEL SERVICIO", "error");
    if (
      !isSameRoute(
        routeValues.destination,
        routeModalList[routeModalList.length - 1],
      )
    )
      return snackBarDialog("EL DESTINO DEBE SER EL ULTIMO PARADERO", "error");

    setAddressDestination(routeValues.destination);

    setIsLoading(true);
    const uuid = uuidv4();
    const nDate = new Date(Date.now());

    //crear backup
    const data = createDataMySql(values, uuid, nDate, routeValues);
    console.log("data", data);
    const backup = await CreateBackupUseCase(data);

    if (backup.message == "success") {
      const orderNumber = backup.insertId;
      const fireData = createDataFirebase(
        values,
        uuid,
        orderNumber,
        nDate,
        routeValues,
      );
      console.log("fireData", fireData);
      const notification = await createBookingFirebase(fireData);
      if (values.booking != 1 && !checked) {
        await sendEmailBooking(values, orderNumber);
      }

      setEnterprise(null);
      setEmployee(null);
      setAddressOrigin(null);
      setAddressDestination(null);
      setRouteModalList([]);
      setOtherPersonName("");
      setOtherPersonPhone("");
      values.persons = 1;
      values.vehicle = "";
      values.note = "";
      if (notification && !notification.success) {
        snackBarDialog(notification.error, "warning");
      } else if (notification?.data?.failureCount > 0) {
        snackBarDialog(
          `PEDIDO CREADO: ${notification.data.successCount} NOTIFICACIONES ENVIADAS, ${notification.data.failureCount} FALLIDAS`,
          "warning",
        );
      } else if (notification?.data?.totalTokens === 0) {
        snackBarDialog(
          "PEDIDO CREADO, SIN TOKENS DE CONDUCTORES CERCANOS PARA NOTIFICAR",
          "warning",
        );
      } else {
        snackBarDialog("PEDIDO CREADO");
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      snackBarDialog("ERROR: NO SE LOGRO CREAR EL SERVICIO", "error");
    }

    // console.log(backup);
  };

  const createNewBooking = async (values) => {
    const routeValues = getRouteValues(routeModalList);

    if (!enterprise) return snackBarDialog("SELECCIONE LA EMPRESA", "error");
    if (!employee) return snackBarDialog("SELECCIONE AL TRABAJADOR", "error");
    if (!addressOrigin.latitude || !addressOrigin.longitude)
      return snackBarDialog("SELECCIONE EL ORIGEN DEL SERVICIO", "error");
    if (!addressDestination.latitude || !addressDestination.longitude)
      return snackBarDialog("SELECCIONE EL DESTINO DEL SERVICIO", "error");

    // setIsLoading(true);
    const uuid = uuidv4();
    const nDate = new Date(Date.now());

    const originModel = new CoordinateModel(
      addressOrigin.name,
      addressOrigin.latitude,
      addressOrigin.longitude,
      addressOrigin.url,
    );
    const destinationModel = new CoordinateModel(
      addressDestination.name,
      addressDestination.latitude,
      addressDestination.longitude,
      addressDestination.url,
    );
    const dateModel = new DateModel(
      values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      values.booking === 3 ? timeFormat(timeEnd.$d) : "",
    );
    const driverModel = new DriverBase(0, 0, values.vehicle);
    const employeeModel = new EmployeeModel(
      employee.id,
      `${employee.name} ${employee.lastname}`,
      employee.email,
      employee.phone,
      phoneActive,
      enterprise.id,
      enterprise.commertial_name,
    );
    const tourModel = new TourModel(
      values.booking === 4 ? tour.tour_id : "",
      values.booking === 4 ? tour.description : "",
    );

    const bookingModel = new BookingModel(
      uuid,
      userType.ENTERPRISE,
      originModel,
      destinationModel,
      dateModel,
      values.persons,
      values.note ? values.note.toUpperCase() : "",
      "",
      createdBy.ADMIN,
      driverModel,
      employeeModel,
      0,
      0,
      tourModel,
      values.booking,
      1,
      travelStatus.PENDING,
      false,
      false,
      false,
      1,
      enterprise.payment_type === "EFECTIVO" ? 1 : 2,
      1,
      tax,
      0,
      priceDriver === "" ? 0 : priceDriver,
      priceTotal === "" ? 0 : priceTotal,
      currencyList.PEN,
      0,
      0,
      values.booking != 1 ? !checked : true,
    );

    // console.log(bookingModel);
    //crear backup
    setIsLoading(true);
    const dataSql = CreateDataSql(bookingModel);
    const backup = await CreateBackupUseCase(dataSql);
    if (backup.message === "success") {
      const orderNumber = backup.insertId;
      const firebaseData = CreateDataFirebase(
        bookingModel,
        orderNumber,
        serviceList,
      );
      await createBookingFirebase(firebaseData);
      if (values.booking != 1 && !checked) {
        await sendEmailBooking(values, orderNumber);
      }
    } else {
      setIsLoading(false);
      snackBarDialog("ERROR: NO SE LOGRO CREAR EL SERVICIO", "error");
    }

    const data = createDataMySql(values, uuid, nDate, routeValues);
    if (backup.message == "success") {
      const orderNumber = backup.insertId;
      const fireData = createDataFirebase(
        values,
        uuid,
        orderNumber,
        nDate,
        routeValues,
      );
      await createBookingFirebase(fireData);

      setEnterprise(null);
      setEmployee(null);
      setAddressOrigin(null);
      setAddressDestination(null);
      setOtherPersonName("");
      setOtherPersonPhone("");
      values.persons = 1;
      values.vehicle = "";
      values.note = "";
      snackBarDialog("PEDIDO CREADO");
      setIsLoading(false);
    } else {
      setIsLoading(false);
      snackBarDialog("ERROR: NO SE LOGRO CREAR EL SERVICIO", "error");
    }
  };

  const createDataMySql = (values, uuid, nDate, routeValues) => {
    const uid = employee.id;
    const destination = routeValues.destination;
    return {
      uuid,
      userType: 0,
      originName: addressOrigin.name,
      originLat: addressOrigin.latitude,
      originLng: addressOrigin.longitude,
      originUrl: addressOrigin.url,
      destinationName: destination.name,
      destinationLat: destination.latitude,
      destinationLng: destination.longitude,
      destinationUrl: destination.url,
      subRoutes: routeValues.subRoutes,
      otherPersonName: otherPersonName,
      otherPersonPhone: otherPersonPhone,
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",
      persons: values.persons,
      vehicleModel: values.vehicle,
      notes: values.note ? values.note.toUpperCase() : "",
      notesAdmin: "",
      userId: uid,
      driverId: 0,
      vehicleId: 0,
      paymentId: 0,
      bookingType: values.booking,
      tourId: values.booking === 4 ? tour.id : 0,
      invoiceId: 0,
      createdBy: "ADMIN",
      status: 1,
      statePayment: "PENDIENTE",
      assigned: false,
      invoice: false,
      invoiceStatus: false,
      travelStatus: 1,
      currency: 1,
      paymentType: enterprise.payment_type === "EFECTIVO" ? 1 : 2,
      paymentVoucher: 1,
      tax: tax,
      toll: 0,
      price: priceDriver === "" ? 0 : priceDriver,
      total: priceTotal === "" ? 0 : priceTotal,
      discount: 0,
      advance: 0,
      quote: values.booking != 1 ? !checked : true,
      companyId: company_id ?? 1,
    };
  };

  const createDataFirebase = (values, uuid, counter, nDate, routeValues) => {
    const uid = employee.id;
    const destination = routeValues.destination;
    return {
      uuid,
      client: Number(uid),
      clientName: `${employee.name} ${employee.lastname}`,
      clientEmail: employee.email,
      clientPhone: employee.phone,
      otherPersonName: otherPersonName,
      otherPersonPhone: otherPersonPhone,
      enterpriseId: enterprise.id,
      enterpriseName: enterprise.commertial_name,
      origin: {
        name: addressOrigin.name,
        lat: addressOrigin.latitude,
        lng: addressOrigin.longitude,
        url: addressOrigin.url,
      },
      destination: {
        name: destination.name,
        lat: destination.latitude,
        lng: destination.longitude,
        url: destination.url,
      },
      subRoutes: routeValues.subRoutes,
      intermediate: serviceList,
      persons: values.persons,
      vehicle: values.vehicle,
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",
      tour_id: values.booking === 4 ? tour.id : 0,
      tour_description: values.booking === 4 ? tour.description : "",
      note: values.note ? values.note.toUpperCase() : "",
      noteAdmin: "",
      currency: 1,
      payment_type: enterprise.payment_type === "EFECTIVO" ? 1 : 2,
      payment_voucher: 1,
      tax: tax === "" ? 0 : Number(tax),
      toll: 0,
      price: priceDriver === "" ? 0 : Number(priceDriver),
      total: priceTotal === "" ? 0 : Number(priceTotal),
      discount: 0,
      advance: 0,
      statusService: "PENDIENTE",
      statusPayment: "PENDIENTE",
      statusBooking: "PENDIENTE",
      createBooking: "ADMIN",
      booking_type: values.booking,
      counterBooking: counter,
      created: Date.now(),
      isCreated: false,
      cotizar: values.booking != 1 ? !checked : true,
      phoneActive: phoneActive,
      companyId: company_id ?? 1,
    };
  };

  // TOUR
  const [tourDialogOpen, setTourDialogOpen] = useState(false);
  const [tour, setTour] = useState(null);
  const [tourLanguage, setTourLanguage] = useState(1);
  const [tourModules, setTourModules] = useState([]);
  const [tourModulesLoading, setTourModulesLoading] = useState(false);
  const [tourModulesLoaded, setTourModulesLoaded] = useState(false);

  useEffect(() => {
    if (!tourDialogOpen || tourModulesLoaded) return;

    let isCurrent = true;
    const loadTourModules = async () => {
      setTourModulesLoading(true);
      try {
        const response = await getListTourModules();
        if (!isCurrent) return;

        const modules = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        setTourModules(modules);
        setTourModulesLoaded(true);
      } catch (error) {
        console.log(error);
      } finally {
        if (isCurrent) setTourModulesLoading(false);
      }
    };

    loadTourModules();
    return () => {
      isCurrent = false;
    };
  }, [tourDialogOpen, tourModulesLoaded]);

  const tourModulesByLanguage = tourModules.filter(
    (module) =>
      Number(module.language_id) === tourLanguage &&
      typeof module.title === "string" &&
      module.title.trim() !== "",
  );

  const [priceTotal, setPriceTotal] = useState(0);
  const [priceDriver, setPriceDriver] = useState(0);
  const [tax, setTax] = useState(0);

  useEffect(() => {
    if (routeRateMode !== "direcciones") {
      return;
    }

    setPriceTotal(0);
    setPriceDriver(0);
    setTax(0);

    if (addressOrigin != null && addressDestination != null) {
      const nameOrigin = addressOrigin.name;
      const nameDestination = addressDestination.name;

      routesEnterprise.map((route) => {
        const checkOriginName = nameOrigin === route.originName;
        const checkDestinationaName = nameDestination === route.destinationName;
        const checkOriRevName = nameOrigin === route.destinationName;
        const checkDestRevName = nameDestination === route.originName;

        if (
          (checkOriginName && checkDestinationaName) ||
          (checkOriRevName && checkDestRevName)
        ) {
          const origin = {
            latitude: route.originLat,
            longitude: route.originLng,
          };
          const destination = {
            latitude: route.destinationLat,
            longitude: route.destinationLng,
          };

          const checkOrigin = distanceBetweenCoordinates(addressOrigin, origin);
          const checkDestination = distanceBetweenCoordinates(
            addressDestination,
            destination,
          );

          const checkOriRev = distanceBetweenCoordinates(
            addressOrigin,
            destination,
          );
          const checkDestRev = distanceBetweenCoordinates(
            addressDestination,
            origin,
          );

          if (
            (checkOrigin && checkDestination) ||
            (checkOriRev && checkDestRev)
          ) {
            if (vehicleSelect == 1) {
              setPriceTotal(Number(route.total));
              setPriceDriver(Number(route.driver));
              setTax(Number(route.tax));
            }
            if (vehicleSelect == 2) {
              setPriceTotal(Number(route.suv_total));
              setPriceDriver(Number(route.suv_driver));
              setTax(Number(route.suv_tax));
            }
            if (vehicleSelect == 3) {
              setPriceTotal(Number(route.mini_total));
              setPriceDriver(Number(route.mini_driver));
              setTax(Number(route.mini_tax));
            }
            if (vehicleSelect == 4) {
              setPriceTotal(Number(route.mini15_total));
              setPriceDriver(Number(route.mini15_driver));
              setTax(Number(route.mini15_tax));
            }
          }
        }
      });
    }
  }, [
    addressOrigin,
    addressDestination,
    routeRateMode,
    routesEnterprise,
    vehicleSelect,
  ]);

  useEffect(() => {
    if (
      routeRateMode !== "ciudades" ||
      addressOrigin == null ||
      addressDestination == null
    ) {
      return;
    }

    const selectedRate = findCityRateForSelectedRoute(
      addressOrigin,
      addressDestination,
    );

    const distanceKm = calculateDistanceKm(addressOrigin, addressDestination);

    if (!selectedRate) {
      if (distanceKm !== null) {
        setPriceTotal(Number((distanceKm * precioKmTemp).toFixed(2)));
      }

      return;
    }

    const routePrice = Number(selectedRate.price);

    if (routePrice > 0) {
      setPriceTotal(routePrice);
      return;
    }

    const priceKm = Number(selectedRate.priceKm);

    if (!Number.isNaN(priceKm) && priceKm >= 0 && distanceKm !== null) {
      setPriceTotal(Number((distanceKm * priceKm).toFixed(2)));
    }
  }, [
    addressOrigin,
    addressDestination,
    findCityRateForSelectedRoute,
    precioKmTemp,
    routeRateMode,
  ]);

  function calculateDistanceKm(pointA, pointB) {
    if (
      pointA?.latitude === null ||
      pointA?.latitude === undefined ||
      pointA?.longitude === null ||
      pointA?.longitude === undefined ||
      pointB?.latitude === null ||
      pointB?.latitude === undefined ||
      pointB?.longitude === null ||
      pointB?.longitude === undefined
    ) {
      return null;
    }

    const earthRadius = 6371;
    const diffLat = toRadians(
      Number(pointB.latitude) - Number(pointA.latitude),
    );
    const diffLng = toRadians(
      Number(pointB.longitude) - Number(pointA.longitude),
    );

    const a =
      Math.sin(diffLat / 2) * Math.sin(diffLat / 2) +
      Math.cos(toRadians(Number(pointA.latitude))) *
        Math.cos(toRadians(Number(pointB.latitude))) *
        Math.sin(diffLng / 2) *
        Math.sin(diffLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = earthRadius * c;

    return Number.isNaN(distance) ? null : distance;
  }

  function distanceBetweenCoordinates(pointA, pointB) {
    const earthRadius = 6371;
    const diffLat = toRadians(pointB.latitude - pointA.latitude);
    const diffLng = toRadians(pointB.longitude - pointA.longitude);

    const a =
      Math.sin(diffLat / 2) * Math.sin(diffLat / 2) +
      Math.cos(toRadians(pointA.latitude)) *
        Math.cos(toRadians(pointB.latitude)) *
        Math.sin(diffLng / 2) *
        Math.sin(diffLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = earthRadius * c;
    return distance <= 0.3;
  }

  function toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  const isCityRateMode = routeRateMode === "ciudades";
  const originLocationOptions = isCityRateMode ? globalCityOptions : addressApi;
  const destinationLocationOptions = isCityRateMode
    ? globalCityOptions
    : addressApi;
  const emptyLocationLabel = isCityRateMode
    ? "Lista de Ciudades"
    : "Lista de Direcciones";
  const getLocationOptionLabel = (option) => {
    if (!option) {
      return "";
    }

    if (isCityRateMode) {
      return option.displayName || option.name || "";
    }

    return `${option.name || ""} ${option.url_address || ""}`.trim();
  };
  const destinationInputText =
    routeModalList.length > 1
      ? `${routeModalList.length} paraderos`
      : getLocationOptionLabel(addressDestination);
  const isSameLocationOption = (option, value) =>
    option?.id === value?.id ||
    (option?.name === value?.name &&
      Number(option?.latitude) === Number(value?.latitude) &&
      Number(option?.longitude) === Number(value?.longitude));
  const getDestinationOptionLabel = (option) =>
    routeModalList.length > 1 &&
    isSameLocationOption(option, addressDestination)
      ? destinationInputText
      : getLocationOptionLabel(option);

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={checkoutSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box
            onSubmit={handleSubmit}
            backgroundColor={colors.primary[400]}
            p={2}
            sx={{ margin: "1px" }}
          >
            <Grid container spacing={1.5} sx={{ alignItems: "center" }}>
              <Grid item xs={12}>
                <FormControl>
                  <RadioGroup
                    row
                    name="routeRateMode"
                    value={routeRateMode}
                    onChange={(event) => setRouteRateMode(event.target.value)}
                  >
                    <FormControlLabel
                      value="direcciones"
                      control={<Radio color="secondary" />}
                      label="Direcciones"
                    />
                    <FormControlLabel
                      value="ciudades"
                      control={<Radio color="secondary" />}
                      label="Ciudades"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {enterprises ? (
                  <Autocomplete
                    value={enterprise}
                    disablePortal
                    name="enterprise"
                    id="combo-box-enterprise"
                    onChange={(e, newValue) => {
                      setEnterprise(newValue);
                      if (newValue?.id) {
                        getEmployees(newValue.id);
                        if (routeRateMode === "direcciones") {
                          getRatesAndRoutes(newValue.id);
                        }
                      }
                    }}
                    options={enterprises}
                    getOptionLabel={(option) =>
                      `${option.commertial_name} - ${option.ruc} - ${option.business_name}`
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={t("Empresas")} />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled
                    type="text"
                    label={t("Lista de Empresas")}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {employees ? (
                  <Autocomplete
                    value={employee}
                    disablePortal
                    name="client"
                    id="combo-box-client"
                    onChange={(e, newValue) => {
                      setEmployee(newValue);
                    }}
                    options={employees}
                    getOptionLabel={(option) =>
                      `${option.name} ${option.lastname} - ${option.phone} - ${option.subrole_name}`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Empleados")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <IconButton
                                size="small"
                                aria-label={t("Agregar empleado")}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenOtherPersonModal();
                                }}
                                sx={{
                                  position: "absolute",
                                  right: 50,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: "text.secondary",
                                }}
                              >
                                <AddIcon fontSize="small" color="success" />
                              </IconButton>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled
                    type="text"
                    label={t("Lista de Empleados")}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {originLocationOptions ? (
                  <Autocomplete
                    value={addressOrigin}
                    disablePortal
                    name="addressOrigin"
                    id="combo-box-origin"
                    onChange={(e, newValue) => {
                      setAddressOrigin(newValue);
                      setAddressDestination(null);
                    }}
                    options={originLocationOptions}
                    getOptionLabel={getLocationOptionLabel}
                    isOptionEqualToValue={isSameLocationOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Origen")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <IconButton
                                size="small"
                                aria-label="ver mapa de origen"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleOpenOriginMapModal}
                                sx={{ mr: 0.5 }}
                              >
                                <MapIcon fontSize="small" color="success" />
                              </IconButton>

                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled
                    type="text"
                    label={t(emptyLocationLabel)}
                  />
                )}
              </Grid>

              {/* {serviceList.map((singleService, index) => (
                <Grid key={index} item xs={12} sm={6}>
                  {destinationLocationOptions ? (
                    <Autocomplete
                      value={addressDestination}
                      disablePortal
                      name="client"
                      id="combo-box-destination"
                      onChange={(e, newValue) => {
                        handleDestinationSelect(newValue);
                      }}
                      options={destinationLocationOptions}
                      getOptionLabel={getDestinationOptionLabel}
                      isOptionEqualToValue={isSameLocationOption}
                      renderInput={(params) => (
                        <TextField {...params} label={t("Destino")} />
                      )}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      disabled
                      type="text"
                      label={t(emptyLocationLabel)}
                    />
                  )}
                </Grid>
              ))} */}

              <Grid item xs={12} sm={6}>
                {destinationLocationOptions ? (
                  <Autocomplete
                    value={addressDestination}
                    disablePortal
                    name="addressDestination"
                    id="combo-box-destination"
                    onChange={(e, newValue) => {
                      handleDestinationSelect(newValue);
                    }}
                    options={destinationLocationOptions}
                    getOptionLabel={getDestinationOptionLabel}
                    isOptionEqualToValue={isSameLocationOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("Destino")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {addressDestination && (
                                <IconButton
                                  size="small"
                                  aria-label="ver rutas"
                                  onMouseDown={(event) =>
                                    event.preventDefault()
                                  }
                                  onClick={handleOpenRoutesModal}
                                  sx={{ mr: 0.4 }}
                                >
                                  <RoutesLottieIcon />
                                </IconButton>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled
                    type="text"
                    label={t(emptyLocationLabel)}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  value={values.persons}
                  fullWidth
                  type="number"
                  label={t("persons")}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="persons"
                  error={!!touched.persons && !!errors.persons}
                  helperText={touched.persons && errors.persons}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("vehicle")}</InputLabel>
                  <Select
                    value={values.vehicle}
                    id="vehicle"
                    name="vehicle"
                    label={t("vehicle")}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setVehicleSelect(e.target.value);
                    }}
                    error={!!touched.vehicle && !!errors.vehicle}
                  >
                    {model.map((model, index) => (
                      <MenuItem key={index} value={model.id}>
                        {t(model.name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={values.booking === 3 ? 4 : 6}
                md={values.booking === 3 ? 4 : 6}
              >
                <FormControl fullWidth>
                  <InputLabel>{"Para:"}</InputLabel>
                  <Select
                    id="booking"
                    value={values.booking}
                    name="booking"
                    label={"Para:"}
                    onChange={(event) => {
                      handleChange(event);
                      if (Number(event.target.value) === 4) {
                        setTourDialogOpen(true);
                      } else {
                        setTourDialogOpen(false);
                      }
                    }}
                  >
                    <MenuItem value={1}>{t("AHORA")}</MenuItem>
                    <MenuItem value={2}>{t("DESPUES")}</MenuItem>
                    <MenuItem value={3}>{t("POR HORA")}</MenuItem>
                    <MenuItem value={4}>{t("TOUR")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={6}
                sm={values.booking === 3 ? 2 : 3}
                sx={{
                  display: {
                    xs:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                    md:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={2}>
                    <MobileDatePicker
                      label={values.booking === 3 ? t("startTime") : t("date")}
                      inputFormat="DD/MM/YYYY"
                      name="date"
                      value={date}
                      onChange={(newValue) => {
                        setDate(newValue);
                        setDateEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={values.booking === 3 ? 2 : 3}
                sx={{
                  display: {
                    xs:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                    md:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={3}>
                    <MobileTimePicker
                      label={values.booking === 3 ? t("startTime") : t("time")}
                      value={time}
                      name="time"
                      onChange={(newValue) => {
                        setTime(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={2}
                sx={{
                  display: {
                    xs: values.booking === 3 ? "block" : "none",
                    md: values.booking === 3 ? "block" : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={2}>
                    <MobileDatePicker
                      label={t("dateEnd")}
                      inputFormat="DD/MM/YYYY"
                      name="dateEnd"
                      value={dateEnd}
                      onChange={(newValue) => {
                        setDateEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={2}
                sx={{
                  display: {
                    xs: values.booking === 3 ? "block" : "none",
                    md: values.booking === 3 ? "block" : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={3}>
                    <MobileTimePicker
                      label={t("timeEnd")}
                      value={timeEnd}
                      name="timeEnd"
                      onChange={(newValue) => {
                        setTimeEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              {values.booking === 4 && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("TOUR")}
                    value={tour ? `${tour.code} - ${tour.title}` : ""}
                    placeholder={t("TOUR")}
                    inputProps={{ readOnly: true }}
                    onClick={() => setTourDialogOpen(true)}
                    sx={{
                      "& .MuiInputBase-input": { cursor: "pointer" },
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  type="text"
                  onChange={handleChange}
                  name="note"
                  value={values.note}
                  fullWidth
                  label={t("notes")}
                  onBlur={handleBlur}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  error={!!touched.note && !!errors.note}
                  helperText={touched.note && errors.note}
                />
              </Grid>

              {!matchesSM && (
                <>
                  <Grid item xs={2} sm={1}>
                    <Checkbox
                      checked={checked}
                      onChange={handleChangeCheckBox}
                      color="success"
                    />
                  </Grid>

                  <Grid item xs={2} sm={1}>
                    <Checkbox
                      checked={phoneActive}
                      onChange={changePhoneActive}
                      color="success"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={4} sm={2}>
                <TextField
                  value={priceDriver}
                  fullWidth
                  type="number"
                  label={"Conductor"}
                  onBlur={handleBlur}
                  onChange={(e) => setPriceDriver(e.target.value)}
                  name="priceDriver"
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <TextField
                  value={tax}
                  fullWidth
                  type="number"
                  label={"Impuesto"}
                  onBlur={handleBlur}
                  onChange={(e) => setTax(e.target.value)}
                  name="tax"
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <TextField
                  value={priceTotal}
                  fullWidth
                  type="number"
                  label={"Total"}
                  onChange={(e) => setPriceTotal(e.target.value)}
                  name="priceTotal"
                />
              </Grid>

              {matchesSM && (
                <Grid item xs={1} sm={1} marginLeft={-1}>
                  <Checkbox
                    checked={checked}
                    onChange={handleChangeCheckBox}
                    color="success"
                  />
                </Grid>
              )}
              <Grid item xs={10} sm={4}>
                <Button
                  fullWidth
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{ height: 45 }}
                  disabled={isLoading}
                >
                  <Typography variant="string">
                    &#128196;&nbsp; {t("booknow")}&nbsp; &#128241;
                    {/* &nbsp;&#128231; */}
                  </Typography>
                </Button>
              </Grid>
              {matchesSM && (
                <>
                  <Grid item xs={1} sm={1}>
                    <Checkbox
                      checked={phoneActive}
                      onChange={changePhoneActive}
                      color="success"
                    />
                  </Grid>
                  {/* <Grid item xs={1} sm={1}>
                    <Checkbox
                      checked={emailActive}
                      onChange={changeEmailActive}
                      color="success"
                    />
                  </Grid> */}
                </>
              )}
            </Grid>
          </Box>
          <SnackBar
            stateSnk={stateSnk}
            vertical={vertical}
            horizontal={horizontal}
            duration={4000}
            handleClose={handleClose}
            severity={severity}
            messageSnk={messageSnk}
          />
          <Dialog
            fullWidth
            maxWidth="sm"
            open={tourDialogOpen}
            onClose={() => setTourDialogOpen(false)}
            PaperProps={{
              sx: {
                height: "80vh",
                maxHeight: "90vh",
              },
            }}
          >
            <DialogTitle>{t("TOUR")}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl>
                  <Typography component="legend">Idioma</Typography>
                  <RadioGroup
                    row
                    name="tour-language"
                    value={String(tourLanguage)}
                    onChange={(event) => {
                      const nextLanguage = Number(event.target.value);
                      setTourLanguage(nextLanguage);
                      setTour((currentTour) =>
                        currentTour &&
                        Number(currentTour.language_id) !== nextLanguage
                          ? null
                          : currentTour,
                      );
                    }}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="ES" />
                    <FormControlLabel value="2" control={<Radio />} label="EN" />
                  </RadioGroup>
                </FormControl>
                <Autocomplete
                  value={tour}
                  disablePortal
                  id="tour-module-selection"
                  onChange={(event, newValue) =>
                    setTour(
                      newValue
                        ? {
                            ...newValue,
                            tour_id: newValue.id,
                            description: newValue.title,
                          }
                        : null,
                    )
                  }
                  options={tourModulesByLanguage}
                  loading={tourModulesLoading}
                  getOptionLabel={(option) =>
                    option ? `${option.code} - ${option.title}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("TOUR")} />
                  )}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={() => setTourDialogOpen(false)}
              >
                {t("Cerrar")}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            fullWidth
            maxWidth="xs"
            open={originMapModalOpen}
            onClose={handleCloseOriginMapModal}
          >
            <DialogTitle>{t("Mapa")}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={originMapAddress}
                  onChange={(event) => setOriginMapAddress(event.target.value)}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
                <TextField
                  fullWidth
                  label="Coordinates"
                  placeholder="latitud,longitud"
                  value={originMapCoordinates}
                  onChange={(event) =>
                    setOriginMapCoordinates(event.target.value)
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleCloseOriginMapModal}
              >
                {t("Cerrar")}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                disabled={originMapSaving}
                onClick={handleSaveOriginMapAddress}
              >
                Guardar
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            fullWidth
            maxWidth="xs"
            open={routesModalOpen}
            onClose={handleCloseRoutesModal}
            PaperProps={{
              sx: {
                height: "80vh",
                maxHeight: "90vh",
              },
            }}
          >
            <DialogTitle>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ alignItems: { xs: "stretch", sm: "center" } }}
              >
                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                  {t("Rutas")}
                </Typography>
                <Autocomplete
                  value={routeModalDestinationToAdd}
                  inputValue={routeModalDestinationInput}
                  disablePortal
                  name="routeDestination"
                  id="combo-box-route-destination"
                  onChange={(event, newValue) => {
                    handleAddRouteDestination(newValue);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "reset") {
                      return;
                    }

                    setRouteModalDestinationInput(newInputValue);
                  }}
                  options={destinationLocationOptions || []}
                  getOptionLabel={getLocationOptionLabel}
                  isOptionEqualToValue={isSameLocationOption}
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Destino")} size="small" />
                  )}
                />
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={1} sx={{ pt: 1 }}>
                <DragDropProvider onDragEnd={handleRouteDragEnd}>
                  <Stack spacing={1}>
                    {routeModalList.map((route, index) => (
                      <RouteSortableItem
                        key={route.routeId}
                        route={route}
                        index={index}
                        onDelete={handleDeleteRouteDestination}
                      />
                    ))}
                  </Stack>
                </DragDropProvider>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleCloseRoutesModal}
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleAcceptRoutes}
              >
                {t("Aceptar")}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            fullWidth
            maxWidth="xs"
            open={otherPersonModalOpen}
            onClose={handleCloseOtherPersonModal}
          >
            <DialogTitle>{"Pedido para Otra Persona"}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label={t("Nombre")}
                  value={otherPersonNameInput}
                  onChange={(event) =>
                    setOtherPersonNameInput(event.target.value.toUpperCase())
                  }
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
                <TextField
                  fullWidth
                  label={t("Telefono")}
                  value={otherPersonPhoneInput}
                  onChange={(event) =>
                    setOtherPersonPhoneInput(event.target.value)
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                onClick={handleCloseOtherPersonModal}
                variant="contained"
                color="error"
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleClearOtherPerson}
                variant="contained"
                color="warning"
              >
                {t("Limpiar")}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleAcceptOtherPerson}
              >
                {t("Aceptar")}
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  );
};

export default BookingBusiness;
